import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconInfoCircle, IconShieldHalfFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { PermissionScope } from '../../hooks/usePermission';
import type { Catalog, CatalogPermission, Grant } from '../../hooks/useRolesApi';
import { canHold, rank } from './shared';
import { ScopeReach } from './ScopeReach';
import classes from './RoleEditor.module.css';

export interface RoleEditorProps {
  catalog: Catalog;
  /** Мои права с сервера: выдать роли можно только их, с тем же или более узким охватом. */
  mine: Partial<Record<string, PermissionScope>>;
  title: string;
  initial: { name: string; description?: string; grants: Grant[] };
  /** Только чтение: «Все права» и вычисляемые роли. */
  readOnlyNote?: string;
  isNew?: boolean;
  isSaving?: boolean;
  onSave?: (body: { name: string; description?: string; grants: Grant[] }) => void;
  onDelete?: () => void;
}

export const RoleEditor: React.FC<RoleEditorProps> = ({
  catalog,
  mine,
  title,
  initial,
  readOnlyNote,
  isNew,
  isSaving,
  onSave,
  onDelete,
}) => {
  const { t } = useTranslation();
  const isReadOnly = !!readOnlyNote;
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description ?? '');
  const [grants, setGrants] = useState(
    () => new Map(initial.grants.map((g) => [g.permission, g.scope])),
  );
  const kept = useMemo(
    () => new Map(initial.grants.map((g) => [g.permission, g.scope])),
    [initial.grants],
  );
  const labelOf = (key: string) => t(`roles.permissions.${key}`);
  const derivedOf = useMemo(
    () =>
      new Map(catalog.permissions.filter((p) => p.derivedFrom).map((p) => [p.derivedFrom!, p.key])),
    [catalog],
  );

  // Охват можно выбрать, если он у меня есть, или сузить тот, что уже был у роли: убрать и
  // сузить может любой, кто раздаёт роли (решение Дениса). Сужение до «Своё» — добавление.
  const isScopeAllowed = (key: string, scope: PermissionScope) => {
    const was = kept.get(key);
    return (
      canHold(mine, { permission: key, scope }) ||
      was === scope ||
      (!!was && scope !== 'self' && rank(was) >= rank(scope))
    );
  };
  // В роли есть права шире моих: их можно убрать или сузить, но не расширить.
  const isWider = !initial.grants.every((g) => canHold(mine, g));

  const set = (key: string, scope: PermissionScope | null) =>
    setGrants((prev) => {
      const next = new Map(prev);
      if (scope) next.set(key, scope);
      else next.delete(key);
      // Производное не шире основного: сужаем вместе с ним, без основного — снимаем.
      const derived = derivedOf.get(key);
      if (derived && next.has(derived) && rank(next.get(derived)) > rank(scope ?? undefined)) {
        if (scope) next.set(derived, scope);
        else next.delete(derived);
      }
      return next;
    });

  const enable = (p: CatalogPermission) => {
    const base = p.derivedFrom ? grants.get(p.derivedFrom) : undefined;
    // Самый широкий из допустимых охватов, но не шире основного права.
    const scope = [...p.scopes]
      .reverse()
      .find((s) => isScopeAllowed(p.key, s) && (!p.derivedFrom || rank(s) <= rank(base)));
    if (!scope) return;
    if (!p.isSensitive) return set(p.key, scope);
    modals.openConfirmModal({
      title: t('roles.editor.sensitiveConfirmTitle'),
      centered: true,
      children: (
        <Text size="sm">{t('roles.editor.sensitiveConfirmText', { name: labelOf(p.key) })}</Text>
      ),
      labels: { confirm: t('roles.editor.sensitiveConfirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => set(p.key, scope),
    });
  };

  const hintOf = (p: CatalogPermission, isOn: boolean): string | undefined => {
    if (isReadOnly) return undefined;
    if (p.derivedFrom && !grants.has(p.derivedFrom))
      return t('roles.editor.needsBase', { name: labelOf(p.derivedFrom) });
    if (!isOn && !p.scopes.some((s) => isScopeAllowed(p.key, s))) return t('roles.editor.notYours');
    return undefined;
  };

  const total = grants.size;
  const onCompany = Array.from(grants.values()).filter((s) => s === 'company').length;
  const sensitive = catalog.permissions.filter((p) => p.isSensitive && grants.has(p.key)).length;

  return (
    <Paper withBorder radius="md" p={{ base: 'md', sm: 'lg' }} className={classes.editor}>
      <Stack gap="md">
        <div>
          <Title order={3}>{title}</Title>
          <Text size="sm" c="dimmed" mt={2} data-testid="role-summary">
            {total
              ? // Нули не пишем: «0 чувствительных» — шум.
                [
                  t('roles.summary.rights', { count: total }),
                  onCompany > 0 && t('roles.summary.company', { count: onCompany }),
                  sensitive > 0 && t('roles.summary.sensitive', { count: sensitive }),
                ]
                  .filter(Boolean)
                  .join(', ')
              : t('roles.summary.empty')}
          </Text>
          {!isReadOnly && isWider && (
            <Text size="sm" c="yellow" mt={4}>
              {t('roles.editor.onlyYours')}
            </Text>
          )}
        </div>
        {readOnlyNote ? (
          <Alert variant="light" color="gray" icon={<IconInfoCircle size={18} />}>
            {readOnlyNote}
          </Alert>
        ) : (
          <>
            <TextInput
              label={t('roles.editor.name')}
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              maxLength={80}
              required
            />
            <Textarea
              label={t('roles.editor.description')}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              maxLength={500}
              autosize
              minRows={1}
            />
          </>
        )}

        {catalog.modules.map((m) => (
          <Box key={m.key}>
            <Divider
              label={
                <Text size="sm" fw={600} c="var(--mantine-color-text)">
                  {t(m.labelKey)}
                </Text>
              }
              labelPosition="left"
              mb="xs"
            />
            <Stack gap={6}>
              {catalog.permissions
                .filter((p) => p.module === m.key)
                .map((p) => {
                  const scope = grants.get(p.key);
                  const isOn = !!scope;
                  const hint = hintOf(p, isOn);
                  const isLocked = isReadOnly || (!!hint && !isOn);
                  return (
                    <Box
                      key={p.key}
                      className={classes.row}
                      data-permission={p.key}
                      data-sensitive={p.isSensitive || undefined}
                      style={{
                        opacity: isLocked && !isOn ? 0.55 : 1,
                        // Чувствительное — тонкая оранжевая линия слева, как щит у названия.
                        ...(p.isSensitive && {
                          borderLeft: '2px solid var(--mantine-color-orange-6)',
                          paddingLeft: 10,
                          marginLeft: -12,
                        }),
                      }}
                    >
                      <Checkbox
                        checked={isOn}
                        disabled={isLocked}
                        onChange={(e) => (e.currentTarget.checked ? enable(p) : set(p.key, null))}
                        label={
                          <Group gap={6} wrap="nowrap">
                            <span>{labelOf(p.key)}</span>
                            {p.isSensitive && (
                              <Tooltip label={t('roles.editor.sensitiveHint')} withArrow>
                                <IconShieldHalfFilled
                                  size={15}
                                  color="var(--mantine-color-orange-5)"
                                  aria-label={t('roles.editor.sensitive')}
                                />
                              </Tooltip>
                            )}
                          </Group>
                        }
                        // Что право открывает — всегда; почему его не включить — второй строкой.
                        description={
                          <>
                            {/* Новое право в каталоге без текста — пустая строка, а не ключ. */}
                            {t(`roles.what.${p.key}`, { defaultValue: '' })}
                            {hint && <span style={{ display: 'block' }}>{hint}</span>}
                          </>
                        }
                      />
                      {/* Охват — только у включённого права: у выключенного он ничего не значит. */}
                      {isOn &&
                        (p.scopes.length > 1 ? (
                          <div className={classes.scope}>
                            <ScopeReach
                              scopes={p.scopes}
                              value={scope}
                              isReadOnly={isReadOnly}
                              isSensitive={p.isSensitive}
                              onChange={(v) => set(p.key, v)}
                              label={t('roles.editor.scopeOf', { name: labelOf(p.key) })}
                              isDisabled={(s) =>
                                !isScopeAllowed(p.key, s) ||
                                (!!p.derivedFrom && rank(s) > rank(grants.get(p.derivedFrom)))
                              }
                            />
                          </div>
                        ) : (
                          <Text size="xs" c="dimmed" className={classes.only}>
                            {t('roles.scopes.company')}
                          </Text>
                        ))}
                    </Box>
                  );
                })}
            </Stack>
          </Box>
        ))}

        {!isReadOnly && (
          <Group justify="space-between" mt="sm">
            {onDelete && !isNew ? (
              <Button variant="subtle" color="red" onClick={onDelete}>
                {t('roles.editor.delete')}
              </Button>
            ) : (
              <span />
            )}
            <Button
              color="teal"
              loading={isSaving}
              disabled={!name.trim()}
              onClick={() =>
                onSave?.({
                  name: name.trim(),
                  description: description.trim() || undefined,
                  grants: Array.from(grants).map(([permission, s]) => ({ permission, scope: s })),
                })
              }
            >
              {isNew ? t('roles.editor.create') : t('common.save')}
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};
